
import React, {createContext, useCallback, useContext, useReducer, useMemo} from "react";
import {Participant, Request, RequestAction, RequestState} from "../models";
import {
    assignResolver,
    cancel,
    fetchRequests,
    fetchParticipants,
    resolve
} from "../services/requests";
import {LoadingState} from "../helper/utils";

const statuses: Request["status"][] = ["declined", "approved", "cancelled"];
type RequestError = {title: string, desc: string};
type ActionHandler = {requestId: string, action: RequestAction};

interface State {
    actionState: RequestState
    currentAction?: RequestAction
    currentId?: string
    error?: RequestError
    requests: Request[]
    status: {actions: LoadingState, overall: LoadingState}
}
type Action =
| {type: "add_request"; payload: Request}
| {type: "set_participants", payload: {id: string, value: Participant[]}}
| {type: "handle_action", payload: ActionHandler}
| {type: "set_requests"; payload: Request[]}
| {type: "set_current_id", payload?: string}
| {type: "set_status"; payload: State["status"]}
| {type: "update_action_state", payload: RequestState}
| {type: "set_action", payload: State["currentAction"]}
| {type: "set_error"; payload?: RequestError};

const initialState: State = {
    actionState: "not-sent",
    requests: [],
    status: {actions: LoadingState.None, overall: LoadingState.None}
};
const RequestContext = createContext<ReturnType<typeof useRequestValue> | undefined>(undefined);

function mapTo(id: string, status: Request["status"], note?: string): (r: Request) => Request {
    return function (r) {
        if (r.requestID === id) {
            return {...r, note, status};
        }
        return r;
    }
}

function translateAction(state: State, props: ActionHandler): State {
    switch(props.action.type) {
        case "add_participants": {
            const {action, requestId} = props;
            return {...state, requests: state.requests.map(function (r) {
                let clone: Request;
                if (r.requestID === requestId) {
                    clone = Object.assign({}, r);
                    clone.recipients = Array.from(new Set(
                        clone.recipients.concat(
                            action.data.users.map((u) => u.email)
                        )
                    ));
                    return clone;
                }
                return r;
            })}
        }
        case "cancel": {
            return {
                ...state,
                requests: state.requests.map(mapTo(props.requestId, "cancelled"))
            };
        }
        case "resolve": {
            const {action:{data:{note, proceed}}, requestId} = props
            let status: Request["status"] = proceed ? "approved": "declined";
            return {
                ...state,
                requests: state.requests.map(mapTo(requestId, status, note)),
            };
        }
        case "reassign": {
            const {requestId, action} = props
            return {
                ...state,
                requests: state.requests.map(function (r) {
                    const recipients = r.recipients.filter(
                        (e) => e !== action.data.email
                    ).concat([r.approver]);
                    if(r.requestID === requestId) {
                        return {...r, approver: action.data.email, recipients};
                    }
                    return r;
                }),
            };
        }
        default:
            return state;
    }
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "add_request":
            return {...state, requests: state.requests.concat([action.payload])};
        case "handle_action":
            return translateAction(state, action.payload);
        case "set_requests":
            return {...state, requests: action.payload};
        case "set_status":
            return {...state, status: action.payload};
        case "set_error":
            return {...state, error: action.payload};
        case "set_current_id":
            return {...state, currentId: action.payload};
        case "set_action":
            return {...state, currentAction: action.payload};
        case "update_action_state":
            return {...state, actionState: action.payload};
        case "set_participants": {
            let requests = state.requests.map(function (req) {
                if (req.requestID === action.payload.id) {
                    return {...req, participants: action.payload.value};
                }
                return req;
            });
            return {...state, requests};
        }
        default:
            return state;
    }
}

function createRequestActions(dispatch: React.Dispatch<Action>) {
    return {
        addRequest: (payload: Request) => dispatch({type: "add_request", payload}),
        handleAction: (payload: ActionHandler) => dispatch(
            {type: "handle_action", payload}
        ),
        setAction: (payload: State["currentAction"]) => dispatch(
            {type: "set_action", payload}
        ),
        setRequests: (payload: Request[]) => dispatch({type: "set_requests", payload}),
        setLoading: (payload: State["status"]) => dispatch({type: "set_status", payload}),
        setError: (payload?: RequestError) => dispatch({type: "set_error", payload}),
        setParticipants: (payload: {value: Participant[], id: string}) => dispatch(
            {type: "set_participants", payload}
        ),
        setRequestId: (payload?: string) => dispatch(
            {type: "set_current_id", payload}
        ),
        updateActionState: (payload: RequestState) => dispatch(
            {type: "update_action_state", payload}
        )
    };
}

type ActionResult = {ok: boolean, code?: string};

function useRequestValue() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const actions = useMemo(() => createRequestActions(dispatch), [dispatch]);
    const currentRequest = useMemo(function () {
        if (!state.currentId) {
            return;
        }
        return state.requests.filter((r) => r.requestID === state.currentId)[0];
    }, [state.currentId, state.requests]);
    const archivedRequests = useMemo(function () {
        return state.requests.filter((r) => statuses.includes(r.status));
    }, [state.requests]);
    const activeRequests = useMemo(function () {
        return state.requests.filter((r) => !statuses.includes(r.status));
    }, [state.requests]);
    const note = useMemo(function () {
        const options = ["approved", "declined"];
        const {note, status} = currentRequest ?? {};
        let state: string = "";
        let name: string;
        let res: Participant;
        if (!currentRequest || !options.includes(status ?? "")) {
            return;
        }
        if (status === "approved") {
            state = "approved";
        }
        if (status === "declined") {
            state = "declined";
        }
        res = currentRequest.participants.filter(
            (p) => p.data.email === currentRequest.approver
        )[0];
        name = (
            res.type === "onboarded"
                ? res.data.name
                : res.data.email.split("@")[0]
        );
        return `Request ${state} by ${name}. ${note ? `with the following note:\n${note}` : ""}`;
    }, [currentRequest]);
    const updateActionStatus = useCallback(function (actions: LoadingState) {
        dispatch({type: "set_status", payload: {...state.status, actions}});
    }, [state.status]);
    const updateOverallStatus = useCallback(function (overall: LoadingState) {
        dispatch({type: "set_status", payload: {...state.status, overall}});
    }, [state.status]);
    const getRequests = useCallback(async function () {
        let res;
        updateOverallStatus(LoadingState.Pending);
        res = await fetchRequests();
        if (res.type === "success") {
            actions.setRequests(res.value);
            updateOverallStatus(LoadingState.Success);
        } else {
            actions.setError(
                {title: "request retrieval Error", desc: res.message}
            );
            updateOverallStatus(LoadingState.Error);
        }
    }, [actions, updateOverallStatus]);
    const selectRequest = useCallback(async function (id?: string) {
        let res;
        actions.setRequestId(id);
        if (id) {
            res = await fetchParticipants(id);
            if (res.type === "success") {
                actions.setParticipants({id, value: res.value});
            } else {
                console.error("Failed to search participants for request ", id);
            }
        }
    }, [actions]);
    const cancelRequest = useCallback(async function (id: string): Promise<ActionResult> {
        let res;
        res = await cancel(id);
        if (res.type === "success") {
            actions.updateActionState("verified");
            return {ok: true};
        }
        if (res.code !== "approval_phone_required") {
            actions.setError({title: "Request cancellation error", desc: res.message});
        }
        return {code: res.code, ok: false};
    }, [actions]);

    const reassignRequest = useCallback(async function (id: string, email: string): Promise<ActionResult> {
        let res;
        res = await assignResolver(id, email);
        if (res.type === "success") {
            actions.updateActionState("verified");
            return {ok: true};
        }
        if (res.code !== "approval_phone_required") {
            actions.setError({title: "Resolver assignment error", desc: res.message});
        }
        return {code: res.code, ok: false};
    }, [actions]);

    const resolveRequest = useCallback(async function (id: string, proceed: boolean, note?: string): Promise<ActionResult> {
        let res;
        res = await resolve({id, proceed, note});
        if (res.type === "success") {
            actions.updateActionState("verified");
            return {ok: true};
        }
        if (res.code !== "approval_phone_required") {
            actions.setError({title: "Request resolution error", desc: res.message});
        }
        return {code: res.code, ok: false};
    }, [actions]);

    /** Run the action: at once, or after the phone approves (when required). */
    const submitAction = useCallback(async function (action: RequestAction): Promise<ActionResult> {
        let res: ActionResult = {ok: false};
        if (!state.currentId) {
            return res;
        }
        updateActionStatus(LoadingState.Error);
        if (action.type === "cancel") {
            res = await cancelRequest(state.currentId);
        }
        if (action.type === "reassign") {
            res = await reassignRequest(state.currentId, action.data.email);
        }
        if (action.type === "resolve") {
            res = await resolveRequest(
                state.currentId,
                action.data.proceed,
                action.data.note
            );
        }
            updateActionStatus(LoadingState.Error);
        return res;
    }, [state.currentId, cancelRequest, reassignRequest, resolveRequest, updateActionStatus]);
    const resetActions = useCallback(function () {
        actions.setAction(undefined);
        dispatch({type: "update_action_state", payload: "not-sent"});
    }, [actions]);
    return useMemo(() => ({
        actions,
        activeRequests,
        archivedRequests,
        currentRequest,
        getRequests,
        note,
        resetActions,
        selectRequest,
        state,
        submitAction
    }), [
        actions,
        activeRequests,
        archivedRequests,
        currentRequest,
        getRequests,
        note,
        resetActions,
        selectRequest,
        state,
        submitAction
    ]);
}

export function RequestProvider({children}: {children: React.ReactNode}) {
    const value = useRequestValue();
    return <RequestContext.Provider value={value}>
        {children}
    </RequestContext.Provider>;
}

export function useRequest() {
    const context = useContext(RequestContext);
    if (!context) {
        throw new Error("useRequest must be used within a RequestProvider");
    }
    return context;
}
